/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PtPermissions, PtUiAppRoutes } from '@pt/pt-models';
import {
  NgrxCoreReducer,
  PermissionManager,
  PtCoreActions,
  PtCoreSelectors,
} from '@pt/pt-core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ErrorInterceptorService } from '@pt/pt-shared';
import { ApiClientConfig, LogService } from '@pt/api-client';
import { UntilDestroy } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'pt-root',
  template: `
    <div [ngStyle]="{visibility: loading || errorInterceptorService.showErrorPage ? 'hidden' : ''}">
      <router-outlet></router-outlet>
    </div>

    <pt-loading-page
      class="position-absolute"
      style="top: 0"
      *ngIf="loading && !errorInterceptorService.showErrorPage"
    ></pt-loading-page>

    <pt-unexpected-error-manager
      [homePath]="homePagePath"
      class="position-absolute w-100"
      style="top: 0"
      *ngIf="errorInterceptorService.showErrorPage"
    ></pt-unexpected-error-manager>

    <pt-chatbot
      *ngIf="(activeModuleChatBot$ | async) && !!username"
      [username]="username">
    </pt-chatbot>
  `,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  public loginResponse$ = this.store.select(PtCoreSelectors.loginResponse);
  public activeModuleChatBot$ = this.store.select(
    PtCoreSelectors.activeModuleChatBot
  );
  private currentActivePermission$ = this.store.select(
    PtCoreSelectors.currentActivePermission
  );
  public loading = false;
  public homePagePath = PtUiAppRoutes.HOME;

  private destroy$ = new Subject();
  public username?: string;

  // la gestione dei temi e delle lingue viene fatta:
  // - nel login component
  // - nel dashboard component
  // così da avere distinto il tema del login con quello della ui
  public isDev = false;

  constructor(
    @Inject(ApiClientConfig) private config: ApiClientConfig,
    private modalService: NgbModal,
    private store: Store<NgrxCoreReducer.CoreState>,
    private renderer: Renderer2,
    public errorInterceptorService: ErrorInterceptorService,
    public permissionManager: PermissionManager,
    private logService: LogService
  ) {
    this.modalService.dismissAll();
    this.loading = true;
    this.isDev = config.env.production === false;
    this.logService.setLogLevel(config.env.logLevel || 'OFF');
    this.store.dispatch(PtCoreActions.loadLoginModules());
  }

  ngOnInit(): void {
    this.loginResponse$.pipe(takeUntil(this.destroy$)).subscribe((u) => {
      this.username = u?.username;
    });

    this.currentActivePermission$
      .pipe(takeUntil(this.destroy$))
      .subscribe((activePermission: PtPermissions[]) => {
        this.permissionManager.alignActivePermission(activePermission);
      });

    // this code stop click event propagation that is in conflict with close
    // details page on click outside and prevent the close action
    // otherwise watch styles.scss on ng-sidebar--animate, ng-sidebar--opened, ng-sidebar--closed
    this.modalService.activeInstances
      .pipe(
        takeUntil(this.destroy$),
        map((instances) => instances as Array<any>)
      )
      .subscribe((modalRefs) => {
        if (modalRefs && modalRefs?.length > 0) {
          modalRefs.map((modalRef) => {
            const modalWindowRef = modalRef?._windowCmptRef
              ?.location as ElementRef;
            const modalNativeElement = modalWindowRef?.nativeElement;
            this.renderer.listen(modalNativeElement, 'click', (event) => {
              event.stopPropagation();
            });
          });
        }
      });

    this.errorInterceptorService
      .listenError()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.modalService.dismissAll();

        const dropdowns = document.querySelectorAll('ng-dropdown-panel');
        dropdowns?.forEach((d) => d.remove());
      });

    this.errorInterceptorService.clean();
  }

  ngAfterViewInit(): void {
    const loadingApp = setTimeout(() => {
      this.loading = false;
      clearTimeout(loadingApp);
    }, 1000);
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.permissionManager.stop();
  }
}
